if (!customElements.get('ds-featured-products')) {
  class DsFeaturedProducts extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.playing = false;
      this._programmaticSlide = false;
      this.init();
    }

    init() {
      const swiperEl = this.querySelector('.ds-fp__slider');
      if (!swiperEl) return;
      if (!window.Swiper) return setTimeout(() => this.init(), 100);

      this.readConfig(swiperEl);
      this.collectElements(swiperEl);
      this.createSwipers(swiperEl);
      this.bindEvents();
      this.updateClasses();
      this.setupAutoplay();
    }

    readConfig(el) {
      const d = el.dataset;
      const num = (v, fb) => {
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : fb;
      };

      this.desktopPV = num(d.spvDesktop, 4);
      this.tabletPV = num(d.spvTablet, 2);
      this.mobilePV = num(d.spvMobile, 1);
      this.gap = num(d.gap, 16);
      this.autoplayDelay = num(d.autoplayDelay, 5000);
      this.autoplayEnabled = d.autoplay === 'true';
      this.totalSlides = num(d.slideCount, 0);
      this.labelPause = this.dataset.labelPause || 'Pause';
      this.labelPlay = this.dataset.labelPlay || 'Play';
    }

    collectElements(swiperEl) {
      this.swiperEl = swiperEl;
      this.thumbsEl = this.querySelector('.ds-fp__thumbs');
      this.slides = Array.from(swiperEl.querySelectorAll('.ds-fp__slide'));
      this.dots = Array.from(this.querySelectorAll('.ds-fp__pagination [data-ds-fp-goto]'));
      this.thumbs = Array.from(this.querySelectorAll('.ds-fp__thumb'));
      this.prevBtns = this.querySelectorAll('[data-ds-fp-prev]');
      this.nextBtns = this.querySelectorAll('[data-ds-fp-next]');
      this.playToggles = this.querySelectorAll('[data-ds-fp-play-toggle]');
    }

    createSwipers(swiperEl) {
      this.mainSwiper = new window.Swiper(swiperEl, {
        slidesPerView: this.mobilePV,
        spaceBetween: this.gap,
        loop: false,
        speed: 500,
        watchSlidesProgress: true,
        breakpoints: {
          640: { slidesPerView: this.tabletPV, spaceBetween: this.gap },
          1050: { slidesPerView: this.desktopPV, spaceBetween: this.gap },
        },
        on: {
          slideChange: () => {
            if (this._programmaticSlide) return;
            this.adoptFrom(this.mainSwiper, /* syncThumbs */ true);
          },
        },
      });

      if (this.thumbsEl && this.thumbs.length > 1) {
        this.thumbsSwiper = new window.Swiper(this.thumbsEl, {
          slidesPerView: 4,
          spaceBetween: 13,
          watchSlidesProgress: true,
          centerInsufficientSlides: true,
          on: {
            slideChange: () => {
              if (this._programmaticSlide) return;
              this.adoptFrom(this.thumbsSwiper, /* syncThumbs */ false);
            },
          },
        });
      }
    }

    clamp(i) {
      return Math.max(0, Math.min(i, this.totalSlides - 1));
    }

    getSpv() {
      const w = window.innerWidth;
      if (w >= 1050) return this.desktopPV;
      if (w >= 640) return this.tabletPV;
      return this.mobilePV;
    }

    updateClasses() {
      const i = this.activeIndex;
      this.slides.forEach((el, idx) => el.classList.toggle('is-active', idx === i));
      this.dots.forEach((el, idx) => el.classList.toggle('is-active', idx === i));
      this.thumbs.forEach((el, idx) => el.classList.toggle('is-active', idx === i));

      const atStart = i <= 0;
      const atEnd = i >= this.totalSlides - 1;
      this.prevBtns.forEach((b) => b.toggleAttribute('disabled', atStart));
      this.nextBtns.forEach((b) => b.toggleAttribute('disabled', atEnd));
    }

    setActive(i) {
      this.activeIndex = this.clamp(i);
      this.updateClasses();
      this._programmaticSlide = true;
      this.ensureMainVisible();
      this.ensureThumbsVisible();
      this._programmaticSlide = false;
    }

    adoptFrom(sourceSwiper, syncThumbs) {
      const newIdx = sourceSwiper.activeIndex;

      if (newIdx === this.activeIndex) return;

      this.userInteracted();
      this.activeIndex = this.clamp(newIdx);
      this.updateClasses();

      this._programmaticSlide = true;

      if (syncThumbs) this.ensureThumbsVisible();
      else this.ensureMainVisible();

      this._programmaticSlide = false;
    }

    ensureMainVisible() {
      if (!this.mainSwiper) return;

      const spv = this.getSpv();
      const first = this.mainSwiper.activeIndex;
      const last = first + spv - 1;

      if (this.activeIndex < first) {
        this.mainSwiper.slideTo(this.activeIndex);
      } else if (this.activeIndex > last) {
        const newFirst = Math.max(0, Math.min(this.activeIndex - spv + 1, this.totalSlides - spv));
        this.mainSwiper.slideTo(newFirst);
      }
    }

    ensureThumbsVisible() {
      if (!this.thumbsSwiper) return;

      const tSpv = 4;
      const first = this.thumbsSwiper.activeIndex;
      const last = first + tSpv - 1;

      if (this.activeIndex < first) {
        this.thumbsSwiper.slideTo(this.activeIndex);
      } else if (this.activeIndex > last) {
        const newFirst = Math.max(0, Math.min(this.activeIndex - tSpv + 1, this.totalSlides - tSpv));
        this.thumbsSwiper.slideTo(newFirst);
      }
    }

    bindEvents() {
      this.prevBtns.forEach((b) => b.addEventListener('click', () => this.go(-1)));
      this.nextBtns.forEach((b) => b.addEventListener('click', () => this.go(+1)));

      const onGoto = (el) => {
        const idx = parseInt(el.dataset.dsFpGoto, 10);

        if (!Number.isFinite(idx)) return;

        this.userInteracted();
        this.setActive(idx);
      };

      this.dots.forEach((d) => d.addEventListener('click', () => onGoto(d)));
      this.thumbs.forEach((t) => t.addEventListener('click', () => onGoto(t)));
    }

    go(step) {
      this.userInteracted();
      this.setActive(this.activeIndex + step);
    }

    setupAutoplay() {
      this.playToggles.forEach((t) => {
        t.addEventListener('click', () => (this.playing ? this.stopAutoplay() : this.startAutoplay()));
      });

      this.addEventListener('mouseenter', () => {
        if (this.playing) this.clearTimer();
      });

      this.addEventListener('mouseleave', () => {
        if (this.playing && !this.autoplayTimer) this.scheduleTick();
      });

      if (this.autoplayEnabled && this.totalSlides > 1) this.startAutoplay();
      else this.setPlaying(false);
    }

    scheduleTick() {
      this.clearTimer();
      this.autoplayTimer = setInterval(() => {
        const next = this.activeIndex >= this.totalSlides - 1 ? 0 : this.activeIndex + 1;

        this.setActive(next);
      }, this.autoplayDelay);
    }

    startAutoplay() {
      this.scheduleTick();
      this.setPlaying(true);
    }

    stopAutoplay() {
      this.clearTimer();
      this.setPlaying(false);
    }

    clearTimer() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    setPlaying(playing) {
      this.playing = playing;
      this.playToggles.forEach((t) => {
        t.dataset.playing = playing ? 'true' : 'false';
        t.setAttribute('aria-label', playing ? this.labelPause : this.labelPlay);
      });
    }

    userInteracted() {
      this.stopAutoplay();
    }
  }

  customElements.define('ds-featured-products', DsFeaturedProducts);
}
